import { useCallback, useEffect, useState } from "react";
import { AttachmentItem } from "./types";

export const DEFAULT_MAX_FILES = 5;
export const DEFAULT_MAX_FILE_SIZE_MB = 10;
export const DEFAULT_ALLOWED_MIME_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "image/jpeg",
  "image/png",
  "image/gif",
  "text/plain",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

export interface UseAttachmentsFieldOptions {
  maxFiles?: number;
  maxFileSizeMB?: number;
  allowedMimeTypes?: string[];
}

export interface UseAttachmentsFieldResult {
  files: File[];
  existingAttachments: AttachmentItem[];
  attachmentsToDelete: number[];
  error: string;
  maxFiles: number;
  maxFileSizeMB: number;
  addFiles: (fileList: FileList | File[]) => void;
  removeFile: (index: number) => void;
  markForDeletion: (attachmentId: number) => void;
  unmarkForDeletion: (attachmentId: number) => void;
  reset: () => void;
}

export function useAttachmentsField(
  existing: AttachmentItem[] | undefined,
  opts?: UseAttachmentsFieldOptions
): UseAttachmentsFieldResult {
  const maxFiles = opts?.maxFiles ?? DEFAULT_MAX_FILES;
  const maxFileSizeMB = opts?.maxFileSizeMB ?? DEFAULT_MAX_FILE_SIZE_MB;
  const allowedMimeTypes = opts?.allowedMimeTypes ?? DEFAULT_ALLOWED_MIME_TYPES;

  const [files, setFiles] = useState<File[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<
    AttachmentItem[]
  >(existing || []);
  const [attachmentsToDelete, setAttachmentsToDelete] = useState<number[]>(
    []
  );
  const [error, setError] = useState<string>("");

  useEffect(() => {
    setExistingAttachments(existing || []);
  }, [existing]);

  const addFiles = useCallback(
    (fileList: FileList | File[]) => {
      const fileArray = Array.from(fileList);
      if (fileArray.length === 0) return;

      const maxFileSizeBytes = maxFileSizeMB * 1024 * 1024;
      const currentFileCount = files.length;
      const existingAttachmentsCount = existingAttachments.length;
      const totalCurrentFiles = currentFileCount + existingAttachmentsCount;

      if (totalCurrentFiles + fileArray.length > maxFiles) {
        const remainingSlots = maxFiles - totalCurrentFiles;
        if (remainingSlots <= 0) {
          setError(
            `Maximum file limit reached! You can only upload up to ${maxFiles} files. Please remove some existing files before adding new ones.`
          );
        } else {
          setError(
            `You can only add ${remainingSlots} more file(s). You currently have ${totalCurrentFiles} files.`
          );
        }
        return;
      }

      const validFiles: File[] = [];
      const errors: string[] = [];

      fileArray.forEach((file) => {
        if (file.size > maxFileSizeBytes) {
          errors.push(
            `${file.name} is too large. Maximum size is ${maxFileSizeMB}MB.`
          );
        } else if (!allowedMimeTypes.includes(file.type)) {
          errors.push(`${file.name} has an unsupported file type.`);
        } else {
          validFiles.push(file);
        }
      });

      if (errors.length > 0) {
        setError(errors.join(", "));
        return;
      }

      setFiles((prev) => [...prev, ...validFiles]);
      setError("");
    },
    [files, existingAttachments, maxFiles, maxFileSizeMB, allowedMimeTypes]
  );

  const removeFile = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const markForDeletion = useCallback((attachmentId: number) => {
    setAttachmentsToDelete((prev) =>
      prev.includes(attachmentId) ? prev : [...prev, attachmentId]
    );
  }, []);

  const unmarkForDeletion = useCallback((attachmentId: number) => {
    setAttachmentsToDelete((prev) => prev.filter((id) => id !== attachmentId));
  }, []);

  const reset = useCallback(() => {
    setFiles([]);
    setAttachmentsToDelete([]);
    setError("");
  }, []);

  return {
    files,
    existingAttachments,
    attachmentsToDelete,
    error,
    maxFiles,
    maxFileSizeMB,
    addFiles,
    removeFile,
    markForDeletion,
    unmarkForDeletion,
    reset,
  };
}

export default useAttachmentsField;
